import { useApolloClient } from '@apollo/react-hooks';

import {
  formatQuizFeedAccuracy,
  formatQuizFeedScore,
  toQuizFeedReportVariables,
} from 'Domains/adaptiveQuiz';
import { useLessonName } from 'Domains/AttendanceFilterData';
import { MASTERY_LEVEL } from 'Domains/mastery';

import { getQuizFeedReport } from '../../GraphQL/queries/report.query';
import { formatSchoolTerm } from '../utils';

import { writeGroupedHeaderWorkbook } from './groupedHeaderWorksheet';

export const ADAPTIVE_QUIZ_REPORT_COLUMNS = [
  { header: 'ห้อง', width: 10 },
  { header: 'หน่วย', width: 30 },
  { header: 'บท', width: 30 },
  { header: 'รหัสนักเรียน', width: 15 },
  { header: 'ชื่อ-นามสกุล', width: 25 },
  {
    group: 'จำนวนข้อสอบที่ทำ (ข้อ)',
    columns: [
      { header: 'ลองทำโจทย์ (ถูก/ทั้งหมด)', width: 22 },
      { header: '%Accuracy', width: 12 },
      { header: 'Adaptive Quiz (ถูก/ทั้งหมด)', width: 22 },
      { header: '%Accuracy', width: 12 },
    ],
  },
  { header: 'ระดับความเชี่ยวชาญ', width: 20 },
];

export const convertAdaptiveQuizReportToAOA = quizFeedReport =>
  quizFeedReport.reports.map(
    ({
      schoolGrade: { shortName: schoolGrade },
      classNo,
      courseCode,
      courseName,
      lessonName,
      classroomUsername,
      studentName,
      tryQuiz,
      adaptiveQuiz,
      mastery,
    }) => [
      `${schoolGrade}/${classNo}`,
      `${courseName} (${courseCode})`,
      lessonName,
      classroomUsername,
      studentName,
      formatQuizFeedScore(tryQuiz),
      formatQuizFeedAccuracy(tryQuiz),
      formatQuizFeedScore(adaptiveQuiz),
      formatQuizFeedAccuracy(adaptiveQuiz),
      MASTERY_LEVEL[mastery]?.name ?? '-',
    ]
  );

const ExportAdaptiveQuizReportData = ({ filterValue, schoolCode, children }) => {
  const client = useApolloClient();
  const { isLessonSelected, lessonName } = useLessonName(filterValue, schoolCode);

  const fetchAdaptiveQuizReportData = async () => {
    // a selected lesson without its name would export all lessons
    if (isLessonSelected && !lessonName) {
      return { error: new Error(`Lesson ${filterValue.lesson} is not in the lesson filter`) };
    }
    try {
      const { data } = await client.query({
        query: getQuizFeedReport,
        variables: { ...toQuizFeedReportVariables({ ...filterValue, lessonName }), schoolCode },
      });
      return { data };
    } catch (error) {
      return { error };
    }
  };

  const exportAdaptiveQuizReport = ({ report: quizFeedReport }) => {
    const {
      schoolTerm: { name: schoolTermName, shortName: schoolTerm } = {},
      subject: { name: subject } = {},
      courseName,
      reports,
    } = quizFeedReport;

    const headerSchoolGrade = reports[0]?.schoolGrade?.shortName ?? '';
    const headerClassNo = toQuizFeedReportVariables(filterValue).classNo ?? `ทุกห้อง`;

    writeGroupedHeaderWorkbook({
      title: `ผลลัพธ์ Adaptive Quiz ${schoolTermName} วิชา${subject}`,
      columns: ADAPTIVE_QUIZ_REPORT_COLUMNS,
      rows: convertAdaptiveQuizReportToAOA(quizFeedReport),
      sheetName: 'Adaptive Quiz',
      fileName: `ผลลัพธ์AdaptiveQuiz_${formatSchoolTerm(
        schoolTerm
      )}_${headerSchoolGrade}-${headerClassNo}_${subject}_${courseName}.xlsx`,
    });
  };

  return children({ fetchAdaptiveQuizReportData, exportAdaptiveQuizReport });
};

export default ExportAdaptiveQuizReportData;
